import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import axiosAuth from "../../axiosAuth";
import { FILE_UPLOAD, SCHOOL_COMMERCIALS_CREATE } from "../apiRoutes";
import { string } from "zod";

export interface CreateSchoolCommercialPayload {
  schoolId: string;
  dealCloserDate: string; // YYYY-MM-DD
  dealAmount: number;
  isGstIncluded: string;
  isPdcCollected: boolean | string; // API might expect Yes/No; we'll normalize
  salesPersonType: string;
  dealClosedBy: string;
  leadType: string;
  contractYear: number;
  paymentFromEntity: string;
  expectedLaunchDate: string; // YYYY-MM-DD
  aggrementDocument?: File | string | null;
}

export interface CreateSchoolCommercialResponse {
  data?: any;
  message?: string;
}

const uploadAgreementAndGetUuid = async (file: File) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("file_type", "aggreement_document");
  const res = await axiosAuth.post(FILE_UPLOAD, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res?.data?.data?.attachment_id as string;
};

export const useCreateSchoolCommercialMutation = (options?: { onSuccess?: () => void }) => {
  return useMutation({
    mutationFn: async (payload: CreateSchoolCommercialPayload) => {
      let agreementUuid: string | undefined;
      if (payload.aggrementDocument instanceof File) {
        agreementUuid = await uploadAgreementAndGetUuid(payload.aggrementDocument);
      }

      const { aggrementDocument, isPdcCollected, isGstIncluded, contractYear, ...rest } = payload as any;

      const bodyWithNulls = {
        ...rest,
        contractYear: Number(contractYear),
        isGstIncluded: String(isGstIncluded),
        isPdcCollected:
          typeof isPdcCollected === "string"
            ? isPdcCollected.toLowerCase() === "yes"
            : Boolean(isPdcCollected),
        aggrementDocument: agreementUuid ?? (typeof aggrementDocument === "string" ? aggrementDocument : undefined),
      } as Record<string, unknown>;

      const body = Object.fromEntries(
        Object.entries(bodyWithNulls).filter(([, v]) => v !== null && v !== undefined && v !== "")
      );

      const response = await axiosAuth.post<CreateSchoolCommercialResponse>(
        SCHOOL_COMMERCIALS_CREATE,
        body
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success("Commercial info saved successfully");
      options?.onSuccess?.();
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.issues?.message || error?.response?.data?.message || "Failed to save commercial info";
      toast.error(message);
    },
  });
};


